/**
 * Compare the second run vs the first (benchmark) for summary table cells.
 * Returns CSS tone for run 2: 'better' | 'worse' | 'neutral'
 */

const EPS = {
  balance: 0.02,
  percent: 0.4,
  roughness: 0.5,
  count: 0,
};

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}

/** Lower distance from 1.0 = better balance (F/R) */
function balanceGoodness(ratio) {
  if (ratio == null) return null;
  return Math.abs(ratio - 1);
}

function inRange(v, lo, hi) {
  return v != null && v >= lo && v <= hi;
}

/** 0 = best (inside [lo,hi]); positive = how far outside in %-points (approx) */
function percentBandDistance(v, lo, hi) {
  if (v == null) return null;
  if (v < lo) return lo - v;
  if (v > hi) return v - hi;
  return 0;
}

const PACKING_RATING_RANK = {
  Excellent: 5,
  Good: 4,
  Moderate: 3,
  Poor: 2,
  Critical: 1,
};

/**
 * @param {string} metricId
 * @param {any} raw1
 * @param {any} raw2
 * @param {object} [cfg] - from getTuningGuideConfig()
 * @returns {'better' | 'worse' | 'neutral' | null}
 */
export function toneSecondVsBenchmark(metricId, raw1, raw2, cfg = {}) {
  const sMin = Number(cfg.tuneDynamicSagPctMin) ?? 25;
  const sMax = Number(cfg.tuneDynamicSagPctMax) ?? 35;
  const bLo = Number(cfg.tuneBalanceRatioMin) ?? 0.75;
  const bHi = Number(cfg.tuneBalanceRatioMax) ?? 1.15;
  const minTravel = Number(cfg.tuneMinTravelUsePct) ?? 70;
  const roughWarn = Number(cfg.tuneRoughnessWarnAbove) ?? 0;

  switch (metricId) {
    case 'balance': {
      const v1 = n(raw1);
      const v2 = n(raw2);
      if (v1 == null || v2 == null) return null;
      if (Math.abs(v2 - v1) < EPS.balance) return 'neutral';
      const d1 = balanceGoodness(v1);
      const d2 = balanceGoodness(v2);
      if (d1 == null || d2 == null) return null;
      // Prefer inside band, else closer to 1.0
      const in1 = inRange(v1, bLo, bHi) ? 1 : 0;
      const in2 = inRange(v2, bLo, bHi) ? 1 : 0;
      if (in2 > in1) return 'better';
      if (in1 > in2) return 'worse';
      if (d2 < d1 - 1e-6) return 'better';
      if (d2 > d1 + 1e-6) return 'worse';
      return 'neutral';
    }
    case 'sagF':
    case 'sagR': {
      const v1 = n(raw1);
      const v2 = n(raw2);
      if (v1 == null || v2 == null) return null;
      if (Math.abs(v2 - v1) < EPS.percent) return 'neutral';
      const p1 = percentBandDistance(v1, sMin, sMax);
      const p2 = percentBandDistance(v2, sMin, sMax);
      // Lower distance to band = better
      if (p2 < p1 - 1e-3) return 'better';
      if (p2 > p1 + 1e-3) return 'worse';
      return 'neutral';
    }
    case 'travelF':
    case 'travelR': {
      const v1 = n(raw1);
      const v2 = n(raw2);
      if (v1 == null || v2 == null) return null;
      if (Math.abs(v2 - v1) < EPS.percent) return 'neutral';
      // Below min use: more travel is better (up toward useful range)
      if (v1 < minTravel && v2 < minTravel) {
        if (v2 > v1) return 'better';
        if (v2 < v1) return 'worse';
        return 'neutral';
      }
      if (v1 < minTravel && v2 >= minTravel) return 'better';
      if (v1 >= minTravel && v2 < minTravel) return 'worse';
      // Both in "enough use" range: no strong good/bad from % alone
      if (v1 >= minTravel && v2 >= minTravel) {
        if (Math.abs(v2 - v1) < EPS.percent * 2) return 'neutral';
        const t = 82;
        const d1 = Math.abs(v1 - t);
        const d2 = Math.abs(v2 - t);
        if (d2 < d1 - 1) return 'better';
        if (d2 > d1 + 1) return 'worse';
        return 'neutral';
      }
      if (v2 > v1) return 'better';
      if (v2 < v1) return 'worse';
      return 'neutral';
    }
    case 'boF':
    case 'boR':
    case 'toF':
    case 'toR': {
      const v1 = n(raw1);
      const v2 = n(raw2);
      if (v1 == null || v2 == null) return null;
      if (v1 === v2) return 'neutral';
      if (v2 < v1) return 'better';
      return 'worse';
    }
    case 'packingPct': {
      const v1 = n(raw1);
      const v2 = n(raw2);
      if (v1 == null || v2 == null) return null;
      if (Math.abs(v2 - v1) < 0.5) return 'neutral';
      if (v2 < v1) return 'better';
      return 'worse';
    }
    case 'packingRating': {
      const a = String(raw1 ?? '').trim();
      const b = String(raw2 ?? '').trim();
      if (!a || !b) return null;
      const r1 = PACKING_RATING_RANK[a];
      const r2 = PACKING_RATING_RANK[b];
      if (r1 == null && r2 == null) return null;
      if (r1 == null || r2 == null) return 'neutral';
      if (r1 === r2) return 'neutral';
      if (r2 > r1) return 'better';
      return 'worse';
    }
    case 'roughness': {
      const v1 = n(raw1);
      const v2 = n(raw2);
      if (v1 == null || v2 == null) return null;
      if (Math.abs(v2 - v1) < EPS.roughness) return 'neutral';
      // When warning threshold is 0, treat "lower = less harsh" only
      if (roughWarn > 0) {
        const bad1 = v1 > roughWarn;
        const bad2 = v2 > roughWarn;
        if (bad1 && !bad2) return 'better';
        if (!bad1 && bad2) return 'worse';
      }
      if (v2 < v1) return 'better';
      if (v2 > v1) return 'worse';
      return 'neutral';
    }
    default:
      return null;
  }
}
